import { ArrowLeft, Wind, Eye, Moon, ChevronRight, Sparkles, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

export const LearnMore = () => {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigateToBook = () => {
    navigate("/book");
    // Ensure scroll to top after navigation
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  return (
    <div className="min-h-screen bg-clarity-bg relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50"></div>
      
      {/* Ambient glow - hidden on mobile */}
      <div className="hidden md:block absolute top-1/4 right-1/4 w-96 h-96 bg-brand-tertiary/10 rounded-full blur-3xl"></div>
      <div className="hidden md:block absolute bottom-1/3 left-1/4 w-80 h-80 bg-brand-primary/10 rounded-full blur-3xl"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 md:p-8 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <Button 
            onClick={() => navigate("/login")}
            data-testid="learn-more-login-btn"
            className="bg-brand-primary text-clarity-bg font-semibold px-6 py-2 rounded-md hover:bg-yellow-400 hover:shadow-[0_0_15px_rgba(229,184,76,0.4)] transition-colors"
          >
            Sign In
          </Button>
        </header>

        {/* Main Content */}
        <main className="px-6 md:px-8 lg:px-16 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            
            {/* Hero Section */}
            <div className="text-center mb-12">
              <p className="font-heading text-lg font-bold text-brand-primary uppercase tracking-wide mb-4">THE CLARITYOS PROTOCOL</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                15 Minutes to Rewire Your Leadership
              </h1>
            </div>

            {/* The Return to Source Section */}
            <div className="glass rounded-2xl p-8 md:p-10 mb-12 border border-brand-tertiary/30">
              <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Sparkles className="w-7 h-7 text-brand-tertiary" />
                The Return to Source
              </h2>
              
              <p className="text-slate-300 text-lg mb-6 leading-relaxed">
                These techniques aren't renamed. They're sourced.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-slate-400 mb-2">What you call <span className="text-white font-medium">"Box breathing"</span>?</p>
                  <p className="text-brand-tertiary font-medium">It's Sama Vritti Pranayam.</p>
                  <p className="text-slate-500 text-sm mt-2">Hatha Yoga Pradipika, 15th century. Same 4-4-4-4 ratio. Same autonomic balance mechanism.</p>
                </div>
                
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-slate-400 mb-2">What you call <span className="text-white font-medium">"The Wim Hof Method"</span>?</p>
                  <p className="text-brand-tertiary font-medium">It's Bhastrika + Tummo.</p>
                  <p className="text-slate-500 text-sm mt-2">Rapid breathing from Hatha Yoga. Tibetan heat generation. Same cold tolerance. Same adrenaline spike.</p>
                </div>
                
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-slate-400 mb-2">What you call <span className="text-white font-medium">"Cyclic sighing"</span> (Andrew Huberman)?</p>
                  <p className="text-brand-tertiary font-medium">It's Rechaka.</p>
                  <p className="text-slate-500 text-sm mt-2">Extended exhale breathing. Documented 3,000 years ago. Same vagal activation. Same cortisol drop.</p>
                </div>
              </div>
              
              <div className="p-6 rounded-xl bg-brand-primary/10 border border-brand-primary/30">
                <p className="text-slate-300 mb-3">
                  Western science keeps "discovering" what the Vedic tradition mapped millennia ago.
                </p>
                <p className="text-slate-300 mb-3">
                  The difference? <span className="text-white font-medium">Now we have the neurochemical proof.</span>
                </p>
                <p className="text-slate-400 text-sm">
                  We can measure the HRV spike. The acetylcholine release. The BDNF expression on brain scans.
                </p>
              </div>
              
              <p className="text-center text-white font-medium text-lg mt-6">
                The protocols were never mystical. They were always biological.
              </p>
            </div>

            {/* Why Leadership Development Fails */}
            <div className="glass rounded-2xl p-8 md:p-10 mb-12">
              <h2 className="font-heading text-2xl font-bold text-white mb-6">
                Why Leadership Development Fails
              </h2>
              
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Every model-transformational, servant, authentic, adaptive-assumes your prefrontal cortex is online.
                </p>
                <p className="text-brand-primary font-medium text-lg">
                  Under pressure? 60-80% of executives operate from their amygdala.
                </p>
                <ul className="space-y-3 my-6">
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>You can't be transformational when you're in threat response.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>You can't be authentic when your hands are cold.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>You can't be adaptive when your brain is binary.</span>
                  </li>
                </ul>
                <p className="text-white font-medium">
                  Leadership isn't downstream of your experience.
                </p>
                <p className="text-white font-medium">
                  It's downstream of your nervous system state.
                </p>
                <p className="text-slate-500 italic mt-4">
                  All the frameworks in the world won't help if the substrate is wrong.
                </p>
              </div>
            </div>

            {/* How It Works - 3 Phases */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-white mb-2">How It Works</h2>
              <p className="text-slate-400 mb-8">3 Phases, 3 Neurochemicals, 15 Minutes</p>
              
              <div className="space-y-6">
                {/* Phase 1: RESET */}
                <div className="glass rounded-xl p-6 border border-brand-tertiary/30">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-brand-tertiary/20 flex items-center justify-center flex-shrink-0">
                      <Wind className="w-7 h-7 text-brand-tertiary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 mb-2">
                        <h3 className="font-heading text-xl font-bold text-brand-tertiary">Phase 1: RESET</h3>
                        <span className="text-slate-500 text-sm">(5 minutes)</span>
                        <span className="text-slate-400 uppercase text-xs tracking-wider">BREATHWORK</span>
                      </div>
                      <p className="text-brand-tertiary/80 font-medium mb-3">Norepinephrine Release + Autonomic Balance</p>
                      <p className="text-slate-400 mb-2"><span className="text-slate-300">What's happening:</span> Your brain slows down. Mental clutter clears.</p>
                      <p className="text-slate-500 italic mb-3">Like shifting from 5th gear to 1st gear.</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-slate-500">Brain state: <span className="text-brand-tertiary">Delta/Theta (1-8 Hz)</span></span>
                      </div>
                      <p className="text-white mt-3"><span className="text-slate-400">Result:</span> The system resets. You drop into feeling mode.</p>
                    </div>
                  </div>
                </div>

                {/* Phase 2: LOCK */}
                <div className="glass rounded-xl p-6 border border-brand-primary/30">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-7 h-7 text-brand-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 mb-2">
                        <h3 className="font-heading text-xl font-bold text-brand-primary">Phase 2: LOCK</h3>
                        <span className="text-slate-500 text-sm">(3 minutes)</span>
                        <span className="text-slate-400 uppercase text-xs tracking-wider">FOCUSED GAZING</span>
                      </div>
                      <p className="text-brand-primary/80 font-medium mb-3">Acetylcholine Surge + Neuroplastic Window</p>
                      <p className="text-slate-400 mb-2"><span className="text-slate-300">What's happening:</span> Your brain narrows its spotlight.</p>
                      <p className="text-slate-500 italic mb-3">Like a camera lens focusing - everything blurry becomes sharp.</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-slate-500">Brain state: <span className="text-brand-primary">Alpha/Beta (8-30 Hz)</span></span>
                        <span className="text-slate-500">Theta/Beta Ratio: <span className="text-brand-secondary">Drops to 0.40 (elite focus)</span></span>
                      </div>
                      <p className="text-white mt-3"><span className="text-slate-400">Result:</span> The scattered "noise" drops. The focused "signal" rises.</p>
                    </div>
                  </div>
                </div>

                {/* Phase 3: INSTALL */}
                <div className="glass rounded-xl p-6 border border-brand-secondary/30">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-brand-secondary/20 flex items-center justify-center flex-shrink-0">
                      <Moon className="w-7 h-7 text-brand-secondary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 mb-2">
                        <h3 className="font-heading text-xl font-bold text-brand-secondary">Phase 3: INSTALL</h3>
                        <span className="text-slate-500 text-sm">(7 minutes)</span>
                        <span className="text-slate-400 uppercase text-xs tracking-wider">DEEP REST</span>
                      </div>
                      <p className="text-brand-secondary/80 font-medium mb-3">BDNF Release + Neural Consolidation</p>
                      <p className="text-slate-400 mb-2"><span className="text-slate-300">What's happening:</span> Your brain wires the new pattern.</p>
                      <p className="text-slate-500 italic mb-3">Like hitting "save" after editing a document.</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-slate-500">Brain state: <span className="text-brand-secondary">Theta + Gamma (4-7 Hz + 30-40 Hz)</span></span>
                      </div>
                      <p className="text-white mt-3"><span className="text-slate-400">Result:</span> New circuits consolidate. This becomes your new default.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center mb-8">
              <Button 
                onClick={() => navigate("/login")}
                className="bg-brand-primary text-clarity-bg font-bold text-lg px-10 py-4 rounded-md hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(229,184,76,0.5)] transition-colors group"
                data-testid="cta-start-protocol"
              >
                Start the Protocol
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform inline" />
              </Button>
            </div>

            {/* Link to Book */}
            <div className="text-center">
              <button
                onClick={handleNavigateToBook}
                className="inline-flex items-center gap-2 text-slate-400 hover:text-brand-primary transition-colors"
                data-testid="link-to-book"
              >
                <BookOpen className="w-5 h-5" />
                <span>Learn more about the upcoming book</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="px-6 md:px-8 py-8 border-t border-white/5 mt-16">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2026 ClarityOS. Optimize your neural operating system.
            </p>
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_clarity-wellness-log/artifacts/dv3nbk4x_Logo%20new%20copy.jpg" 
                alt="ClarityOS" 
                className="w-8 h-8 rounded-lg object-cover"
              />
              <span className="text-lg tracking-tight" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                <span className="font-bold text-white">CLARITY</span>
                <span className="font-normal text-brand-primary">OS</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
